import { Link } from 'react-router-dom';
import { site } from '../../content/site';
import { publicConfig } from '../../lib/config';
import { fillTemplate } from '../../lib/format';
import { Container } from './Container';

/**
 * A value that has not been supplied by the deployment.
 *
 * Rendered as a marked chip rather than an empty line: a public site with no
 * contact address should fail review loudly, and a blank space does not.
 */
function UnsetValue({ label }: { label: string }) {
  return (
    <span className="mt-1 inline-block rounded border border-signal bg-signal/30 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-warm">
      {label}
    </span>
  );
}

export function Footer() {
  const { contactEmail, mailingAddress } = publicConfig;
  const copyright = fillTemplate(site.footer.copyrightTemplate, {
    year: new Date().getFullYear(),
    brand: site.brandName,
  });

  return (
    <footer className="w-full bg-ink text-field">
      <Container className="py-12 sm:py-16">
        <div className="flex flex-col gap-10 sm:flex-row sm:justify-between">
          <div className="max-w-sm">
            <p className="text-lg font-semibold tracking-tight">{site.brandName}</p>
            <p className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-warm">
              {site.tagline}
            </p>
            <p className="mt-4 text-sm leading-relaxed text-field/75">{site.footer.note}</p>
          </div>

          <div className="flex flex-col gap-8 sm:flex-row sm:gap-16">
            <nav aria-label={site.footer.navLabel}>
              <ul className="flex flex-col gap-3 text-sm">
                {site.footer.links.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="text-field underline underline-offset-4 hover:text-warm"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="text-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-warm">
                {site.footer.contactLabel}
              </p>
              {contactEmail ? (
                <p className="mt-2">
                  <a
                    href={`mailto:${contactEmail}`}
                    className="text-field underline underline-offset-4 hover:text-warm"
                  >
                    {contactEmail}
                  </a>
                </p>
              ) : (
                <UnsetValue label={site.footer.contactUnset} />
              )}

              <p className="mt-6 text-xs font-semibold uppercase tracking-[0.16em] text-warm">
                {site.footer.addressLabel}
              </p>
              {mailingAddress ? (
                <p className="mt-2 max-w-xs leading-relaxed text-field/85">{mailingAddress}</p>
              ) : (
                <UnsetValue label={site.footer.addressUnset} />
              )}

              {contactEmail && mailingAddress ? null : (
                <p className="mt-3 max-w-xs text-xs leading-relaxed text-field/60">
                  {site.footer.unsetHint}
                </p>
              )}
            </div>
          </div>
        </div>

        <p className="mt-10 border-t border-field/15 pt-6 text-sm text-field/70">{copyright}</p>
      </Container>
    </footer>
  );
}
